// Toast global de lembrete de programa. Monta no AppProviders — aparece
// em qualquer rota (Home, Live, Search, Settings, Player, etc), não só
// no LiveScreen como era antes.
//
// Bug que motivou (2026-05-15): se user estava fora do LiveScreen quando
// um lembrete disparava, ele perdia o toast — não tinha como saber que
// o programa estava começando. Pré-requisito pra feature de PIP/espiar
// (item 2 do backlog pós-v1.0.0).
//
// IMPORTANTE: useFiringReminder roda poll 30s + consumeFiringReminders.
// Esse hook NÃO PODE ser chamado em mais de 1 lugar — `consume` é
// destrutivo (lê + remove). Se houver 2 instâncias rodando, vão
// competir e às vezes a outra pega o disparo e o toast nem aparece.

import { useTranslation } from '@app-iptv/i18n'
import { Focusable, setFocus } from '@app-iptv/ui'

import { useCanUsePip } from '../hooks/useCanUsePip'
import { useChannelGroup } from '../hooks/useChannelGroup'
import { useChannelProbe } from '../hooks/useChannelProbe'
import { usePipStore } from '../pip-store'
import { router } from '../routes'
import { useFiringReminder } from '../screens/live/useReminderToast'

import { IconBell, IconPip } from './PlayerIcons'
import styles from './ReminderToastGlobal.module.css'

export function ReminderToastGlobal() {
  const { t } = useTranslation()
  const { firingReminder, dismissReminder } = useFiringReminder()
  // ⚠️ NOT useNavigate: este componente é montado em AppProviders FORA do
  // <RouterProvider> (precisamos que ele apareça mesmo quando o user
  // está em rota pública, pré-login). useNavigate() exige contexto de
  // Router. router.navigate() é imperativo e funciona em qualquer lugar.
  const navigate = (path: string) => void router.navigate(path)

  // Probe Xtream pra decidir se o botão "Espiar" (PiP) aparece. Só
  // sondamos quando o toast está visível pra não consumir rede à toa.
  // Probe roda 1× por canal disparado — se canal estiver off/blocked,
  // botão "Espiar" não renderiza (UX honesta — evita abrir PiP que
  // não vai tocar). Botão "Assistir" continua sempre (fullscreen pode
  // dar erro nativo e usuário sabe lidar).
  const canUsePip = useCanUsePip()
  const { status: probeStatus } = useChannelProbe(
    firingReminder?.channelId ?? null,
    !!firingReminder && canUsePip, // skip probe se PiP não vai aparecer
  )
  const channelGroup = useChannelGroup(firingReminder?.channelId ?? null)
  // "Espiar" exige: capability OK (tier mid+), probe ok, e group
  // resolvido. Em vlow/low (Tizen <5, webOS <5, FireTV <7, RAM <2GB)
  // o botão simplesmente não aparece — 2 streams simultâneos travariam
  // o device. UX silencioso (sem msg "não suportado").
  const peekAvailable = canUsePip && probeStatus === 'ok' && channelGroup !== null
  const openPip = usePipStore((s) => s.open)

  if (!firingReminder) return null

  return (
    <div className={styles.reminderToast} role="status" aria-live="polite">
      <div className={styles.reminderToastText}>
        <div className={styles.reminderToastTitle}>
          <span className={styles.reminderToastIcon}>
            <IconBell size={14} />
          </span>
          <span className={styles.reminderToastTitleText}>
            {firingReminder.programTitle}
          </span>
        </div>
        <div className={styles.reminderToastSubtitle}>
          {firingReminder.channelName} —{' '}
          {firingReminder.tier === '15min'
            ? t('live.reminder15min')
            : firingReminder.tier === '5min'
              ? t('live.reminder5min')
              : t('live.reminderNowText')}
        </div>
      </div>
      <div className={styles.reminderToastActions}>
        <Focusable
          focusKey="reminder-banner-go"
          onEnterPress={() => {
            navigate(`/player/${firingReminder.channelId}`)
            dismissReminder()
          }}
          onArrowPress={(dir) => {
            if (dir === 'right') {
              setFocus(peekAvailable ? 'reminder-banner-peek' : 'reminder-banner-dismiss')
              return false
            }
            return false
          }}
          className={styles.reminderToastBtn}
        >
          {t('live.reminderWatch')}
        </Focusable>
        {peekAvailable && (
          <Focusable
            focusKey="reminder-banner-peek"
            onEnterPress={() => {
              if (channelGroup) {
                openPip(firingReminder.channelId, channelGroup)
                dismissReminder()
              } else {
                // Safety fallback — não deveria acontecer porque
                // peekAvailable já checa channelGroup !== null.
                navigate(`/player/${firingReminder.channelId}`)
                dismissReminder()
              }
            }}
            onArrowPress={(dir) => {
              if (dir === 'left') {
                setFocus('reminder-banner-go')
                return false
              }
              if (dir === 'right') {
                setFocus('reminder-banner-dismiss')
                return false
              }
              return false
            }}
            className={styles.reminderToastPeek}
            aria-label={t('live.reminderPeek')}
          >
            <IconPip size={14} />
            <span>{t('live.reminderPeek')}</span>
          </Focusable>
        )}
        <Focusable
          focusKey="reminder-banner-dismiss"
          onEnterPress={() => dismissReminder()}
          onArrowPress={(dir) => {
            if (dir === 'left') {
              setFocus(peekAvailable ? 'reminder-banner-peek' : 'reminder-banner-go')
              return false
            }
            return false
          }}
          className={styles.reminderToastDismiss}
        >
          {t('common.cancelAction')}
        </Focusable>
      </div>
    </div>
  )
}
